import { useState } from 'react';
import { AlertTriangle, Clock, User, Truck, ChevronDown, ChevronUp, MessageSquare, Phone, Hand } from 'lucide-react';
import clsx from 'clsx';
import type { AlertEvent } from '../../types/alert';
import { SEVERITY_LABELS, SEVERITY_COLORS, CATEGORY_LABELS } from '../../utils/constants';
import { formatDate, formatRelative } from '../../utils/formatters';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { EmergencyCallModal } from './EmergencyCallModal';
import { AlertEvidenceList } from './AlertEvidenceList';
import { useDrivers } from '../../hooks/useDrivers';

interface AlertCardProps {
  alert: AlertEvent;
  onClose?: (id: string) => void;
}

export function AlertCard({ alert, onClose }: AlertCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const { drivers } = useDrivers();
  const driverPhone = drivers.find(d => d.id === alert.driverId)?.phone;

  const color = SEVERITY_COLORS[alert.severity];

  return (
    <>
      <article
        className={clsx('alert-card', `alert-card--${alert.severity}`, alert.status === 'closed' && 'alert-card--closed')}
        style={{ borderLeftColor: color }}
        id={`alert-card-${alert.id}`}
      >
        {/* Header */}
        <div className="alert-card__header" onClick={() => setExpanded(e => !e)} role="button" tabIndex={0}>
          <div className="alert-card__header-left">
            {alert.isEscalated && (
              <span className="alert-card__escalated" title="Escalada">🔺</span>
            )}
            <Badge
              variant={alert.severity === 'critical' ? 'critical' : alert.severity === 'medium' ? 'medium' : 'low'}
              pulse={alert.status === 'active' && alert.severity === 'critical'}
            >
              {SEVERITY_LABELS[alert.severity]}
            </Badge>
            <span className="alert-card__category">{CATEGORY_LABELS[alert.category]}</span>
            {/* FE-17/BE-45: una alerta `human` la declaró una persona; vale lo
                que valga su palabra. Una `device` la midió el sensor. El
                supervisor tiene que ver la diferencia ANTES de decidir qué
                hacer, así que va en la cabecera, no escondida al expandir. */}
            {alert.source === 'human' && (
              <span className="alert-card__source" title="Declarada por una persona, no medida por el equipo">
                <Hand size={12} aria-hidden /> Declarada por una persona
              </span>
            )}
            {alert.subCategory && (
              <span className="alert-card__subcategory">— {alert.subCategory}</span>
            )}
          </div>
          <div className="alert-card__header-right">
            <span className="alert-card__time">{formatRelative(alert.timestamp)}</span>
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        </div>

        {/* Meta row */}
        <div className="alert-card__meta">
          <span className="alert-card__meta-item">
            <Truck size={13} /> {alert.vehiclePlate}
          </span>
          {alert.driverName && (
            <span className="alert-card__meta-item">
              <User size={13} /> {alert.driverName}
            </span>
          )}
          <span className="alert-card__meta-item">
            <Clock size={13} /> {formatDate(alert.timestamp)}
          </span>
          <span className="alert-card__subunit">{alert.subUnitName}</span>
        </div>

        {/* Expanded */}
        {expanded && (
          <div className="alert-card__expanded">

            {/* Evidencia real (BE-52): el video se pide al expandir, nunca por fila */}
            <AlertEvidenceList alertId={alert.id} fileStem={`evidencia-${alert.vehiclePlate}`} />

            {/* Closure info */}
            {alert.status === 'closed' && (
              <div className="alert-card__closure">
                <p className="alert-card__closure-title">✓ Cerrada por {alert.closedBy}</p>
                <p className="alert-card__closure-reason">"{alert.closureReason}"</p>
                {alert.closedAt && <p className="alert-card__closure-date">{formatDate(alert.closedAt)}</p>}
              </div>
            )}

            {/* Actions (only Call, Chat, and Resolver — no Atender, no Expandir) */}
            {alert.status === 'active' && (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem', borderTop: '1px solid var(--border)', paddingTop: '0.75rem' }}>
                {alert.driverName && (
                  <>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowCallModal(true);
                      }}
                      leftIcon={<Phone size={14} />}
                      style={{ background: '#10B981', borderColor: '#10B981' }}
                    >
                      Llamar
                    </Button>

                    <Button
                      variant="primary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.dispatchEvent(new CustomEvent('open-chat', { detail: { driverId: alert.driverId, driverName: alert.driverName } }));
                      }}
                      leftIcon={<MessageSquare size={14} />}
                    >
                      Ir al Chat
                    </Button>
                  </>
                )}

                {onClose && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => onClose(alert.id)}
                    leftIcon={<AlertTriangle size={14} />}
                  >
                    Resolver / Cerrar
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </article>

      {/* Emergency Call Dialer Modal */}
      <EmergencyCallModal
        isOpen={showCallModal}
        onClose={() => setShowCallModal(false)}
        driverName={alert.driverName || 'Conductor'}
        driverPhone={driverPhone}
        vehiclePlate={alert.vehiclePlate}
      />
    </>
  );
}
